import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useCan from "../../../../Hooks/useCan";
import { CompanyContext } from "../../../../Contexts/CompanyContext";
import useLoading from "../../../../Hooks/useLoading";
import useModal from "../../../../Hooks/useModal";
import { ProjectModel } from "../../../../Models/ProjectModel";
import { LinesModel } from "../../../../Models/LinesModel";
import { AuthContext } from "../../../../Contexts/AuthContext";
import { IResponseProps } from "../../../../Interfaces/IResponseProps";
import { DeleteProjectLinesAPI, GetAllAssignedLinesByProjectIdAPI } from "../../../../API/RecommendationAPI";
import { GetProjectAPI, PutJustificationOnProjectAPI } from "../../../../API/ProjectsAPI";
import ModalLines from "../ModalLines";
import CardHeader from "../../../components/CardHeader";
import { AutoComplete, Button, Input, Space, Table, TableProps } from "antd";
import { EyeOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import ModalDelete from "../../../components/ModalDelete";

const ProjectLines = () => {
    const canView = useCan({ rolesCan: ["Manager", "Consultant", "Comercial", "Trainee", "Marketing"] });
    const canAddLine = useCan({ rolesCan: ["Consultant", "Administrator"] });
    const canDelete = useCan({ rolesCan: ["Manager", "Consultant"] })
    const { id, projectId } = useParams();
    const { company } = useContext(CompanyContext);
    const { loading, toggleLoading } = useLoading();
    const { showModal, toggleShowModal } = useModal();
    const [project, setProject] = useState<ProjectModel>();
    const [lines, setLines] = useState<LinesModel[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [justification, setJustification] = useState<string>("");
    const [search, setSearch] = useState<string>("");
    const [valueHasChanged, setValueHasChanged] = useState<boolean>(false);
    const { userEmail } = useContext(AuthContext);
    const [take, setTake] = useState<number>(7);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [selectedLineToDelete, setSelectedLineToDelete] = useState<LinesModel | null>(null);
    const navigate = useNavigate();

    const getAllLinesByProject = async (page?: number) => {
        try {
            toggleLoading(true);

            let text = search == null || search == "" ? "all" : search;

            await GetAllAssignedLinesByProjectIdAPI(projectId ?? "", page ?? 1, take, text).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {

                    setLines(resp.data.items);
                    setTotal(resp.data.total);
                }
            }).finally(() => {
                if (valueHasChanged) {
                    setValueHasChanged(false);
                }
                toggleLoading(false);
            });
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const getProject = async (id: string) => {
        try {
            toggleLoading(true);

            await GetProjectAPI(id).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setProject(resp.data);
                    setJustification(resp.data.justification);
                }
            }).finally(() => {
                toggleLoading(false);
            });
        } catch (error: any) {
            toggleLoading(false);
            toast.error(error);
        }
    }

    const putJustification = async () => {
        try {
            toggleLoading(true);

            await PutJustificationOnProjectAPI(project?.id ?? "", justification, userEmail, company.id).finally(() => toggleLoading(false));
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const deleteProjectLine = async (lineId: string) => {
        try {
            toggleLoading(true);

            await DeleteProjectLinesAPI(projectId ?? "", lineId, company.id).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    getAllLinesByProject();
                }
            }).finally(() => toggleLoading(false));
        } catch (error: any) {
            toggleLoading(false);
            toast.error(error);
        }
    }

    useEffect(() => {
        if (projectId) {
            getProject(projectId).then(() => getAllLinesByProject());
        }
    }, [projectId]);

    useEffect(() => {
        if (valueHasChanged) {
            getAllLinesByProject();
        }
    }, [valueHasChanged, take]);

    function goToLine(line: LinesModel) {
        navigate(`/grantsSearcher/grants/${line.idLine}`,{state:
            {title: line.favoritesGrantsName,
            typeOfFinancing: line.typeOfFinancing} });
    }
    
    const projectLinesColumns: TableProps<LinesModel>['columns'] = [
        {
            key: "favoritesGrantsName",
            dataIndex: "favoritesGrantsName",
            showSorterTooltip: { target: 'full-header' },
            filters: lines.map(item => ({
                text: item.favoritesGrantsName,
                value: item.favoritesGrantsName,
            })),
            title: "Nome da Linha",
            width: '50%'
        },
        {
            key: "favoritesGrantsName",
            dataIndex: "favoritesGrantsName",
            title: "Ações",
            width: '5%',
            render: (_, record) => (
                <Space size="small">
                    <Button type="text" size="small" onClick={() => goToLine(record)}><EyeOutlined className="blue" /></Button>
                    {canDelete && (
                        <Button type="text" size="small" onClick={() => {setSelectedLineToDelete(record); setShowDeleteModal(true)}} danger > <DeleteOutlined /></Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className='card ml-2 mr-2'>
            <ModalLines
                setValueHasChanged={setValueHasChanged}
                projectId={projectId ?? ""}
                show={showModal}
                toggleShow={toggleShowModal}
                companyId={company.id}
            />
            <ModalDelete
                show={showDeleteModal}
                toggleShow={() => setShowDeleteModal(!showDeleteModal)}
                setResponse={(resp) => {
                    if (resp && selectedLineToDelete) {
                        deleteProjectLine(selectedLineToDelete.id);
                    }
                    setSelectedLineToDelete(null)
                }}
                itemName={`linha: "${selectedLineToDelete?.favoritesGrantsName || ''}"`}
            />
            <CardHeader
                pageName={project?.name ?? ""}
                hasPageBefore={true}
                pageBeforeName={"Recomendação"}
                linkPageBefore={`/recommendations/${id}`}
            />
            <div className='card-body mt-2 mb-2 w-100 h-100'>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <AutoComplete
                        popupMatchSelectWidth={true}
                        className="w-100 pe-0"
                        onSearch={(val) => { setSearch(val) }}
                    >
                        <Input.Search onBlur={() => getAllLinesByProject()} showCount={false} className="white" loading={loading} placeholder="Procurar" enterButton />
                    </AutoComplete>
                    {canAddLine &&
                        <Button icon={<PlusOutlined />} className="ms-3" type="primary" color="primary" onClick={toggleShowModal}>
                            Adicionar Linhas
                        </Button>
                    }
                </div>
                <Table
                    loading={loading}
                    showHeader={false}
                    rowKey="id"
                    pagination={{
                        pageSizeOptions: ['7', '10', '25', '50', '75', '100'],
                        showSizeChanger: true,
                        total: total,
                        showTotal: (total) => `Total ${total} items`,
                        pageSize: take,
                        onChange: (page, pageSize) => {
                            getAllLinesByProject(page)
                        },
                        onShowSizeChange: (page, size) => {
                            setTake(size)
                        }
                    }}
                    dataSource={lines}
                    columns={projectLinesColumns}
                />
                <div className="w-100 mt-2">
                    <label className="required mb-1">Justificativa FI </label>
                    <TextArea
                        value={justification}
                        disabled={!canView}
                        onChange={(e) => setJustification(e.target.value)}
                        className="form-control "
                        placeholder="Insira uma justificativa.."
                        onBlur={putJustification}
                        rows={4}
                        maxLength={undefined}
                    />
                </div>
            </div>
        </div>
    )
}

export default ProjectLines;